import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { MultiSelect } from "primereact/multiselect";
import { ProgressSpinner } from "primereact/progressspinner";
import BusinessCustomersActions from "../../Redux/transition/customers/business-customer.reducer";
import CustomToast from "../../Components/CustomToast";
import "../../Styles/pages/product.scss";
import "../../Styles/components/segment.scss";
import "../../Styles/components/tags.scss";

const SpecifyCriteriaDialog = ({
    visible,
    onHide,
    audienceId,
    businessId,
    initialSegmentIds = [],
    initialTagIds = [],
    onSaved,
}) => {
    const { t } = useTranslation("common");
    const dispatch = useDispatch();

    const [selectedSegmentIds, setSelectedSegmentIds] = useState([]);
    const [selectedTagIds, setSelectedTagIds] = useState([]);
    const [saveClicked, setSaveClicked] = useState(false);
    const [toastTrigger, setToastTrigger] = useState(false);
    const [toastData, setToastData] = useState({ summary: "", severity: "", detail: "", life: 0 });

    const {
        segmentSuccess,
        tagGroupsGraphed,
        fetchingSegment,
        fetchingTagGroupsGraphed,
        savingAudienceCandidateMetadata,
        audienceCandidateMetadataSaved,
        saveAudienceCandidateMetadataError,
    } = useSelector((state) => state.businessCustomers);

    const getSegments = useCallback(
        (id) =>
            dispatch(
                BusinessCustomersActions.fetchingSegmentRequest(id, {
                    page: 0,
                    size: 1000,
                    sort: "createdDate,desc",
                })
            ),
        [dispatch]
    );
    const getTagGroups = useCallback(
        (id) => dispatch(BusinessCustomersActions.tagGroupGraphedRequest(id)),
        [dispatch]
    );
    const saveCriteria = useCallback(
        (id, metadata) => dispatch(BusinessCustomersActions.saveAudienceCandidateMetadataRequest(id, metadata)),
        [dispatch]
    );

    useEffect(() => {
        if (visible && businessId) {
            getSegments(businessId);
            getTagGroups(businessId);
            setSelectedSegmentIds(initialSegmentIds);
            setSelectedTagIds(initialTagIds);
            setSaveClicked(false);
        }
    }, [visible, businessId, getSegments, getTagGroups, initialSegmentIds, initialTagIds]);

    useEffect(() => {
        if (!savingAudienceCandidateMetadata && audienceCandidateMetadataSaved && saveClicked) {
            onSaved?.();
            setSaveClicked(false);
            setToastData({
                summary: t("success"),
                severity: "success",
                detail: t("business_customers.save_criteria_success"),
                life: 3000,
            });
            setToastTrigger(true);
        }
        if (!savingAudienceCandidateMetadata && saveAudienceCandidateMetadataError && saveClicked) {
            setToastData({
                summary: t("err"),
                severity: "error",
                detail: t("business_customers.save_criteria_error"),
                life: 4000,
            });
            setToastTrigger(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savingAudienceCandidateMetadata, audienceCandidateMetadataSaved, saveAudienceCandidateMetadataError, saveClicked]);

    const segments = useMemo(() => {
        const raw = segmentSuccess;
        if (Array.isArray(raw)) {
            return raw;
        }
        if (raw?.content && Array.isArray(raw.content)) {
            return raw.content;
        }
        return [];
    }, [segmentSuccess]);

    const segmentOptions = useMemo(
        () =>
            segments
                .filter((segment) => segment?.id && (segment?.name || segment?.label))
                .map((segment) => ({
                    label: segment.name ?? segment.label,
                    value: segment.id,
                })),
        [segments]
    );

    const tagOptions = useMemo(() => {
        if (!tagGroupsGraphed?.groups) {
            return [];
        }

        return tagGroupsGraphed.groups
            .filter((group) => group?.tags?.length)
            .map((group) => ({
                label: group.name,
                items: (group.tags || [])
                    .filter((tag) => tag?.id && tag?.name)
                    .map((tag) => ({ label: tag.name, value: tag.id })),
            }))
            .filter((group) => group.items.length > 0);
    }, [tagGroupsGraphed]);

    const handleClose = () => {
        if (savingAudienceCandidateMetadata) {
            return;
        }
        setSaveClicked(false);
        onHide();
    };

    const handleSave = () => {
        if (!selectedSegmentIds.length && !selectedTagIds.length) {
            return;
        }
        setSaveClicked(true);
        saveCriteria(audienceId, { segmentIds: selectedSegmentIds, tagIds: selectedTagIds });
    };

    const canSave = selectedSegmentIds.length > 0 || selectedTagIds.length > 0;
    const criteriaLoading = fetchingSegment || fetchingTagGroupsGraphed;

    return (
        <Dialog
            header={t("business_customers.specify_criteria_title")}
            visible={visible}
            style={{ width: "350px" }}
            onHide={handleClose}
        >
            <CustomToast
                {...toastData}
                trigger={toastTrigger}
                handleActionsAfterToast={() => {
                    setToastTrigger(false);
                    setSaveClicked(false);
                    handleClose();
                }}
                handleActionsAfterErrorToast={() => {
                    setToastTrigger(false);
                    setSaveClicked(false);
                }}
            />

            <div className="p-fluid add-main-div">
                <p className="normal_hint mb-3">{t("business_customers.specify_criteria_hint")}</p>

                <div className="mb-1">
                    <label htmlFor="criteria_segment_select" className="tag_label">
                        {t("business_customers.candidate_segments")}
                    </label>
                </div>
                {fetchingSegment ? (
                    <ProgressSpinner style={{ width: "24px", height: "24px" }} />
                ) : segmentOptions.length ? (
                    <MultiSelect
                        id="criteria_segment_select"
                        className="w-full specify-criteria-multiselect specify-criteria-segment-multiselect"
                        panelClassName="segment-multiselect-panel"
                        value={selectedSegmentIds}
                        onChange={(e) => setSelectedSegmentIds(e.value || [])}
                        options={segmentOptions}
                        optionLabel="label"
                        optionValue="value"
                        placeholder={t("segment.segment_label")}
                        showSelectAll={false}
                        display="chip"
                    />
                ) : (
                    <p className="segment_hint mb-0">{t("business_customers.audience_no_criteria")}</p>
                )}

                <div className="mb-1 mt-3">
                    <label htmlFor="criteria_tag_select" className="tag_label">
                        {t("business_customers.candidate_tags")}
                    </label>
                </div>
                {fetchingTagGroupsGraphed ? (
                    <ProgressSpinner style={{ width: "24px", height: "24px" }} />
                ) : tagOptions.length ? (
                    <MultiSelect
                        id="criteria_tag_select"
                        className="tag_select custom-multiselect specify-criteria-multiselect"
                        panelClassName="tag-multiselect-panel"
                        value={selectedTagIds}
                        onChange={(e) => setSelectedTagIds(e.value || [])}
                        options={tagOptions}
                        optionLabel="label"
                        optionValue="value"
                        optionGroupLabel="label"
                        optionGroupChildren="items"
                        placeholder={t("tags.tag_select_")}
                        showSelectAll={false}
                        display="chip"
                    />
                ) : (
                    <p className="segment_hint mb-0">{t("business_customers.audience_no_criteria")}</p>
                )}

                <div className="p-field p-mb-3 mt-4">
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "2rem" }}>
                        <Button
                            label={t("subscriber.cancel")}
                            className="mr-1"
                            icon="pi pi-times"
                            onClick={handleClose}
                            id="cancel-tag-button"
                            type="button"
                            disabled={savingAudienceCandidateMetadata}
                        />
                        <Button
                            type="button"
                            label={savingAudienceCandidateMetadata ? t("business_customers.saving_criteria") : t("business_customers.save_criteria")}
                            className="p-mt-2 save-btns"
                            onClick={handleSave}
                            disabled={!canSave || savingAudienceCandidateMetadata || criteriaLoading}
                            loading={savingAudienceCandidateMetadata}
                        />
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default SpecifyCriteriaDialog;
